/** NWS API period shape (forecast + hourly). */
export interface NwsPeriod {
  number: number;
  name: string;
  startTime: string;
  isDaytime: boolean;
  temperature: number;
  temperatureUnit: string;
  probabilityOfPrecipitation: { value: number | null } | null;
  shortForecast: string;
  detailedForecast: string;
  icon: string;
  windSpeed: string;
  windDirection: string;
}

export interface NwsDayCard {
  /** Calendar date in property timezone (YYYY-MM-DD). */
  dateKey: string;
  /** e.g. "Today", "Tue" */
  dayLabel: string;
  /** e.g. "6/1" */
  dateLabel: string;
  highF: number | null;
  lowF: number | null;
  precipChance: number | null;
  shortForecast: string;
  iconUrl: string;
}

export interface NwsHourCard {
  startTime: string;
  label: string;
  tempF: number;
  precipChance: number | null;
  shortForecast: string;
  iconUrl: string;
}

export function precipValue(p: NwsPeriod): number | null {
  const v = p.probabilityOfPrecipitation?.value;
  return typeof v === 'number' ? v : null;
}

/** Group alternating day/night NWS periods into scrollable day cards (up to 10). */
export function groupForecastDays(periods: NwsPeriod[], timeZone: string, now = new Date()): NwsDayCard[] {
  const cards: NwsDayCard[] = [];
  let i = 0;
  while (i < periods.length && cards.length < 10) {
    const dayPeriod = periods[i]!;
    const nightPeriod = periods[i + 1];
    const dayStart = parseInstant(dayPeriod.startTime);
    const dateKey = formatDateKey(dayStart, timeZone);
    const todayKey = formatDateKey(now, timeZone);
    const tomorrowKey = formatDateKey(addDays(now, 1), timeZone);

    let dayLabel: string;
    if (dateKey === todayKey) dayLabel = 'Today';
    else if (dateKey === tomorrowKey) dayLabel = 'Tomorrow';
    else dayLabel = weekdayShort(dayStart, timeZone);

    const highF = dayPeriod.temperature;
    const lowF =
      nightPeriod && !nightPeriod.isDaytime ? nightPeriod.temperature : dayPeriod.isDaytime ? null : dayPeriod.temperature;

    const precipCandidates = [precipValue(dayPeriod)];
    if (nightPeriod) precipCandidates.push(precipValue(nightPeriod));
    const precipChance = maxNullable(precipCandidates);

    cards.push({
      dateKey,
      dayLabel,
      dateLabel: monthDayLabel(dayStart, timeZone),
      highF,
      lowF,
      precipChance,
      shortForecast: dayPeriod.shortForecast,
      iconUrl: scaleIconUrl(dayPeriod.icon),
    });

    i += nightPeriod && !nightPeriod.isDaytime ? 2 : 1;
  }
  return cards;
}

/** Next N hours for horizontal hourly strip. */
export function groupHourlyPeriods(periods: NwsPeriod[], timeZone: string, count = 24, now = new Date()): NwsHourCard[] {
  return periods.slice(0, count).map(p => {
    const start = parseInstant(p.startTime);
    const label = hourLabel(start, timeZone, now);
    return {
      startTime: p.startTime,
      label,
      tempF: p.temperature,
      precipChance: precipValue(p),
      shortForecast: p.shortForecast,
      iconUrl: scaleIconUrl(p.icon),
    };
  });
}

export function scaleIconUrl(url: string): string {
  if (!url) return url;
  return url.replace(/size=small/, 'size=medium');
}

function parseInstant(iso: string): Date {
  return new Date(iso);
}

function formatDateKey(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

function weekdayShort(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(d);
}

function monthDayLabel(d: Date, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', { timeZone, month: 'numeric', day: 'numeric' }).format(d);
}

function hourLabel(d: Date, timeZone: string, now: Date): string {
  const diffMin = Math.round((d.getTime() - now.getTime()) / 60_000);
  if (diffMin >= -30 && diffMin < 30) return 'Now';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour: 'numeric',
    hour12: true,
  }).format(d);
}

function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setDate(next.getDate() + days);
  return next;
}

function maxNullable(values: (number | null)[]): number | null {
  const nums = values.filter((v): v is number => v != null);
  return nums.length ? Math.max(...nums) : null;
}

function resolveWpcPath(src: string, baseUrl: string): string {
  const trimmed = src.trim();
  if (trimmed.startsWith('http')) return trimmed;
  return `${baseUrl}${trimmed.startsWith('/') ? '' : '/'}${trimmed.replace(/^\//, '')}`;
}

function wpcMapLabel(pathOrUrl: string): string {
  return pathOrUrl.includes('168') ? '7-day total' : '5-day total';
}

/** Parse WPC multi-day QPF HTML for 5- and 7-day map image URLs (full-size GIFs when linked). */
export function parseWpcQpfMapUrls(html: string, baseUrl = 'https://www.wpc.ncep.noaa.gov'): WpcMapEntry[] {
  const out: WpcMapEntry[] = [];
  const push = (src: string) => {
    const path = src.trim();
    if (!path.includes('p120') && !path.includes('p168')) return;
    const url = resolveWpcPath(path, baseUrl);
    const label = wpcMapLabel(path);
    if (!out.some(e => e.url === url)) out.push({ label, url });
  };

  const hrefRx = /href="([^"]*\/qpf\/p(?:120|168)i\.gif[^"]*)"/gi;
  let m: RegExpExecArray | null;
  while ((m = hrefRx.exec(html)) !== null) push(m[1]!);
  if (out.length >= 2) return out.slice(0, 2);

  const rx = /<td[^>]*class="[^"]*qpf_tab[^"]*"[^>]*>[\s\S]*?<img[^>]+src="([^"]+)"[^>]*>[\s\S]*?<\/td>/gi;
  while ((m = rx.exec(html)) !== null) push(m[1]!);
  if (out.length >= 2) return out.slice(0, 2);

  const imgRx = /src="(\/qpf\/p(?:120|168)i[^"]+)"/gi;
  while ((m = imgRx.exec(html)) !== null) push(m[1]!);
  return out.slice(0, 2);
}

export interface WpcMapEntry {
  label: string;
  url: string;
}
